import React from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Bar,
  BarChart,
  Cell,
  Pie,
  PieChart,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
  ResponsiveContainer,
} from 'recharts';

import type { ChartCategory } from '../../constants/dashboardChartCategories';
import { buildDistributionChartData } from '../../utils/buildDistributionChartData';

/** Geometria única do donut — mesma proporção em tipo de mudança e tipo de norma. */
const DONUT_INNER_RADIUS = 30;
const DONUT_OUTER_RADIUS = 48;
const DONUT_RING_RATIO = DONUT_INNER_RADIUS / DONUT_OUTER_RADIUS;
const CHARTS_ROW_HEIGHT = 148;
const CHART_TITLE_OFFSET = 22;
const TABLE_HEADER_HEIGHT = 36;
const TABLE_BODY_ROW_HEIGHT = 27;

interface DistributionChartsPanelProps {
  categories: readonly ChartCategory[];
  rawCounts: Record<string, number> | undefined;
  tableTypeColumnLabel?: string;
  /** Limita linhas visíveis na tabela; demais entradas rolam (ex.: card tipo de norma). */
  tableMaxVisibleRows?: number;
  /** Mensagem quando não há dados no período/filtro. */
  emptyHint?: string;
}

type PieLabelProps = {
  cx?: number;
  cy?: number;
  midAngle?: number;
  outerRadius?: number;
  value?: number;
  index?: number;
};

type PieSlice = { name: string; value: number; color: string };

function getVisibleLabelIndices(slices: PieSlice[], singleSlice: boolean): Set<number> {
  if (singleSlice) return new Set([0]);
  return new Set(
    slices
      .map((slice, index) => ({ index, value: Number(slice.value) || 0 }))
      .filter((slice) => slice.value > 0)
      .map((slice) => slice.index),
  );
}

function shouldShowSliceLabel(
  value: number | undefined,
  singleSlice: boolean,
  index: number | undefined,
  visibleIndices: Set<number>,
): boolean {
  if (singleSlice) return true;
  return visibleIndices.has(index ?? -1);
}

function renderDonutPercentLabel(
  baseFontSize: number,
  labelOffset: number,
  singleSlice: boolean,
  visibleIndices: Set<number>,
) {
  return (props: PieLabelProps) => {
    const { cx = 0, cy = 0, midAngle = 0, outerRadius = 0, value, index } = props;
    if (!shouldShowSliceLabel(value, singleSlice, index, visibleIndices)) return null;

    const numericValue = Number(value ?? 0);
    const text = `${numericValue.toFixed(1)}%`;

    if (singleSlice) {
      return (
        <text
          x={cx}
          y={cy}
          textAnchor="middle"
          dominantBaseline="central"
          fontSize={Math.max(12, baseFontSize + 1)}
          fontWeight={800}
          fill="#404040"
        >
          {text}
        </text>
      );
    }

    const fontSize = numericValue < 2 ? Math.max(9, baseFontSize - 1) : baseFontSize;
    const offset = numericValue < 2 ? labelOffset + 3 : labelOffset;
    const RADIAN = Math.PI / 180;
    const cos = Math.cos(-midAngle * RADIAN);
    const sin = Math.sin(-midAngle * RADIAN);
    const radius = outerRadius + offset;
    const x = cx + radius * cos;
    const y = cy + radius * sin;
    const textAnchor = cos > 0.12 ? 'start' : cos < -0.12 ? 'end' : 'middle';

    return (
      <text
        x={x}
        y={y}
        textAnchor={textAnchor}
        dominantBaseline="central"
        fontSize={fontSize}
        fontWeight={700}
        fill="#404040"
      >
        {text}
      </text>
    );
  };
}

function renderDonutLabelLine(
  labelOffset: number,
  singleSlice: boolean,
  visibleIndices: Set<number>,
) {
  return (props: PieLabelProps) => {
    const { cx = 0, cy = 0, midAngle = 0, outerRadius = 0, value, index } = props;
    if (singleSlice || !shouldShowSliceLabel(value, singleSlice, index, visibleIndices)) return <g />;

    const numericValue = Number(value ?? 0);
    const offset = numericValue < 2 ? labelOffset + 3 : labelOffset;
    const RADIAN = Math.PI / 180;
    const cos = Math.cos(-midAngle * RADIAN);
    const sin = Math.sin(-midAngle * RADIAN);
    const sx = cx + (outerRadius + 2) * cos;
    const sy = cy + (outerRadius + 2) * sin;
    const ex = cx + (outerRadius + offset - 3) * cos;
    const ey = cy + (outerRadius + offset - 3) * sin;

    return <line x1={sx} y1={sy} x2={ex} y2={ey} stroke="#c8c8c8" strokeWidth={1} />;
  };
}

function computePieMargins(singleSlice: boolean) {
  if (singleSlice) {
    return { top: 8, right: 8, bottom: 8, left: 8 };
  }
  return { top: 14, right: 18, bottom: 14, left: 18 };
}

interface DonutPercentChartProps {
  data: PieSlice[];
  singleSlice: boolean;
  hasData: boolean;
  emptyColor: string;
  height: number;
  labelFontSize: number;
  labelOffset: number;
}

function DonutPercentChart({
  data,
  singleSlice,
  hasData,
  emptyColor,
  height,
  labelFontSize,
  labelOffset,
}: DonutPercentChartProps) {
  const theme = useTheme();
  const pieData = hasData ? data : [{ name: 'Nenhuma', value: 1, color: emptyColor }];
  const visibleLabelIndices = React.useMemo(
    () => getVisibleLabelIndices(pieData, singleSlice),
    [pieData, singleSlice],
  );
  const margins = computePieMargins(singleSlice);

  return (
    <Box
      sx={{
        height,
        width: '100%',
        overflow: 'visible',
        '& .recharts-wrapper': { overflow: 'visible' },
        '& svg': { overflow: 'visible' },
      }}
    >
      <ResponsiveContainer width="100%" height="100%">
        <PieChart margin={margins}>
          <Pie
            data={pieData}
            cx="50%"
            cy="50%"
            innerRadius={DONUT_INNER_RADIUS}
            outerRadius={DONUT_OUTER_RADIUS}
            startAngle={singleSlice ? 90 : 0}
            endAngle={singleSlice ? -270 : 360}
            paddingAngle={singleSlice ? 0 : 1.5}
            dataKey="value"
            isAnimationActive={false}
            stroke={singleSlice ? pieData[0]?.color : undefined}
            strokeWidth={singleSlice ? 1 : 0}
            label={
              hasData
                ? renderDonutPercentLabel(labelFontSize, labelOffset, singleSlice, visibleLabelIndices)
                : false
            }
            labelLine={
              hasData && !singleSlice
                ? renderDonutLabelLine(labelOffset, singleSlice, visibleLabelIndices)
                : false
            }
          >
            {pieData.map((entry, index) => (
              <Cell key={`p-${index}`} fill={entry.color} stroke={entry.color} />
            ))}
          </Pie>
          <RechartsTooltip
            contentStyle={{
              backgroundColor: theme.palette.background.paper,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 10,
              boxShadow: '0px 8px 18px rgba(0,0,0,0.08)',
            }}
            formatter={(value: number) => [`${Number(value).toFixed(1)}%`, '']}
          />
        </PieChart>
      </ResponsiveContainer>
    </Box>
  );
}

export function DistributionChartsPanel({
  categories,
  rawCounts,
  tableTypeColumnLabel = 'Tipo',
  tableMaxVisibleRows,
  emptyHint,
}: DistributionChartsPanelProps) {
  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down('md'));
  const { legend, quantity, percent } = buildDistributionChartData(categories, rawCounts);
  const hasPercentData = percent.length > 0;
  const quantityChart = React.useMemo(
    () => quantity.filter((item) => item.value > 0),
    [quantity],
  );
  const sliceCount = hasPercentData ? percent.length : 0;
  const singleSlice = sliceCount === 1;
  const chartsRowHeight = singleSlice
    ? (isNarrow ? CHARTS_ROW_HEIGHT + 8 : CHARTS_ROW_HEIGHT)
    : (isNarrow ? CHARTS_ROW_HEIGHT + 32 : CHARTS_ROW_HEIGHT + 24);
  const chartPlotHeight = chartsRowHeight - CHART_TITLE_OFFSET;
  const labelOffset = 11;
  const labelFontSize = 11;
  const tableScrollMaxHeight = tableMaxVisibleRows
    ? TABLE_HEADER_HEIGHT + tableMaxVisibleRows * TABLE_BODY_ROW_HEIGHT
    : undefined;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1 }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          columnGap: 1.5,
          rowGap: 1.5,
          alignItems: 'stretch',
          minHeight: chartsRowHeight,
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: chartsRowHeight }}>
          <Typography
            sx={{ fontSize: 12, fontWeight: 800, color: '#7a7a7a', textTransform: 'uppercase' }}
            display="block"
            mb={0.5}
          >
            Quantidade
          </Typography>
          <Box sx={{ flex: 1, minHeight: chartPlotHeight }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={quantityChart.length ? quantityChart : [{ name: '—', value: 0, color: theme.palette.grey[300] }]}
                layout="vertical"
                margin={{ top: 4, right: 8, bottom: 4, left: 0 }}
              >
                <XAxis
                  type="number"
                  allowDecimals={false}
                  tick={{ fontSize: 10, fill: '#808080', fontWeight: 600 }}
                  stroke="#b0b0b0"
                />
                <YAxis type="category" dataKey="name" width={24} tick={false} />
                <RechartsTooltip
                  contentStyle={{
                    backgroundColor: theme.palette.background.paper,
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 10,
                    boxShadow: '0px 8px 18px rgba(0,0,0,0.08)',
                  }}
                  formatter={(value: number) => [value, 'Mudanças']}
                />
                <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                  {(quantityChart.length ? quantityChart : [{ color: theme.palette.grey[300] }]).map((entry, index) => (
                    <Cell key={`q-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            minWidth: 0,
            minHeight: chartsRowHeight,
            overflow: 'visible',
          }}
        >
          <Typography
            sx={{ fontSize: 12, fontWeight: 800, color: '#7a7a7a', textTransform: 'uppercase' }}
            display="block"
            mb={0.5}
          >
            Percentual
          </Typography>
          <DonutPercentChart
            data={percent}
            singleSlice={singleSlice}
            hasData={hasPercentData}
            emptyColor={theme.palette.grey[300]}
            height={chartPlotHeight}
            labelFontSize={labelFontSize}
            labelOffset={labelOffset}
          />
        </Box>
      </Box>

      <Box>
        <Typography
          sx={{ fontSize: 12, fontWeight: 800, color: '#7a7a7a', textTransform: 'uppercase' }}
          display="block"
          mb={0.5}
        >
          Tabela de cores (tipos fixos)
        </Typography>
        <TableContainer
          sx={{
            maxHeight: tableScrollMaxHeight,
            overflowY: tableMaxVisibleRows ? 'auto' : 'visible',
          }}
        >
          <Table
            size="small"
            stickyHeader={Boolean(tableMaxVisibleRows)}
            sx={{ tableLayout: 'fixed', '& .MuiTableCell-root': { fontSize: '0.75rem' } }}
          >
            <TableHead>
              <TableRow sx={{ height: TABLE_HEADER_HEIGHT }}>
                <TableCell sx={{ py: 0.25, fontWeight: 600, width: '28%', bgcolor: 'background.paper' }}>
                  {tableTypeColumnLabel}
                </TableCell>
                <TableCell sx={{ py: 0.25, fontWeight: 600, width: '18%', bgcolor: 'background.paper' }}>
                  Cor
                </TableCell>
                <TableCell sx={{ py: 0.25, fontWeight: 600, width: '27%', bgcolor: 'background.paper' }} align="right">
                  Qtd
                </TableCell>
                <TableCell sx={{ py: 0.25, fontWeight: 600, width: '27%', bgcolor: 'background.paper' }} align="right">
                  %
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {legend.map((row) => (
                <TableRow key={row.key} sx={{ height: TABLE_BODY_ROW_HEIGHT }}>
                  <TableCell sx={{ py: 0.25 }}>{row.label}</TableCell>
                  <TableCell sx={{ py: 0.25 }}>
                    <Box
                      sx={{
                        width: 14,
                        height: 14,
                        borderRadius: 0.5,
                        bgcolor: row.color,
                        border: '1px solid',
                        borderColor: 'divider',
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ py: 0.25 }} align="right">
                    {row.value}
                  </TableCell>
                  <TableCell sx={{ py: 0.25 }} align="right">
                    {row.percent.toFixed(1)}%
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
      {!hasPercentData && emptyHint ? (
        <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#808080', textAlign: 'center', mt: 0.5 }}>
          {emptyHint}
        </Typography>
      ) : null}
    </Box>
  );
}

// Exportado para testes / documentação da proporção fixa do anel.
export const DONUT_GEOMETRY = {
  innerRadius: DONUT_INNER_RADIUS,
  outerRadius: DONUT_OUTER_RADIUS,
  ringRatio: DONUT_RING_RATIO,
  chartsRowHeight: CHARTS_ROW_HEIGHT,
} as const;
